import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import * as apiMethods from "../res/apiMethods.js";

// ─── Iconos MUI ───
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DescriptionIcon from '@mui/icons-material/Description';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const Home = () => {
  const [creditosData, setCreditosData] = useState([]);
  const [transaccionesData, setTransaccionesData] = useState([]);
  const [clientesData, setClientesData] = useState([]);
  const [activeTab, setActiveTab] = useState('creditos');
  const [loading, setLoading] = useState(true);

  // ─── Auth: lógica original ───
  const actUsrToken = window.sessionStorage.getItem("localToken");
  let roleId = null;
  try {
    if (actUsrToken) {
      roleId = JSON.parse(atob(actUsrToken.split(".")[1])).roleId;
    }
  } catch (e) { roleId = null; }

  // ─── Data fetching: misma lógica original con GET_CREDITO_ALL ───
  useEffect(() => {
    document.title = "Inicio";

    if (!actUsrToken) return;

    axios
      .get(apiMethods.GET_CREDITO_ALL, {
        headers: { Authorization: actUsrToken },
      })
      .then((response) => {
        const creditos = response.data.credits || [];

        // Extraer transacciones (misma lógica original)
        const todasTransacciones = [];
        creditos.forEach((credito) => {
          if (credito.transacciones && Array.isArray(credito.transacciones)) {
            const transaccionesConCredito = credito.transacciones.map((tx) => ({
              ...tx,
              credito: credito.id,
            }));
            todasTransacciones.push(...transaccionesConCredito);
          }
        });

        // Extraer clientes (misma lógica original)
        const todosClientes = [];
        creditos.forEach((credito) => {
          if (credito.deudores && Array.isArray(credito.deudores)) {
            todosClientes.push(...credito.deudores);
          }
        });

        setCreditosData(creditos);
        setTransaccionesData(todasTransacciones);
        setClientesData(todosClientes);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // ─── Helpers de formato (lógica original) ───
  const formatearMonto = (valor) => {
    const numero = typeof valor === 'string' ? parseFloat(valor.replace(/,/g, '')) : valor;
    if (isNaN(numero)) return '$0';
    return `$${numero.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatearFecha = (fechaISO) => {
    const meses = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
    const fecha = new Date(fechaISO);
    return `${fecha.getDate()}-${meses[fecha.getMonth()]}-${fecha.getFullYear()}`;
  };

  // ─── KPIs calculados desde los datos reales ───
  const creditosVigentes = creditosData.filter((c) => {
    const capital = typeof c.vlrCapital === 'string' ? parseFloat(c.vlrCapital.replace(/,/g, '')) : c.vlrCapital;
    return capital > 0;
  });

  const totalCartera = creditosVigentes.reduce((sum, c) => {
    const capital = typeof c.vlrCapital === 'string' ? parseFloat(c.vlrCapital.replace(/,/g, '')) : c.vlrCapital;
    return sum + (isNaN(capital) ? 0 : capital);
  }, 0);

  // Pagos de la última semana (7 días)
  const ultimosPagos = (() => {
    const hoy = new Date();
    const hace7Dias = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 7);

    return transaccionesData
      .filter(tx => {
        if (tx.vlrTx === 0 || tx.vlrTx === '0') return false;
        const fechaTx = new Date(tx.fechaTx);
        return fechaTx >= hace7Dias;
      })
      .sort((a, b) => new Date(b.fechaTx) - new Date(a.fechaTx));
  })();

  const metricCards = [
    {
      id: 'cartera',
      label: 'Cartera Total Administrada',
      prefix: '$',
      value: totalCartera.toLocaleString('es-CO', { minimumFractionDigits: 0 }),
      suffix: 'COP',
      subtitle: `${creditosVigentes.length} créditos vigentes con saldo > 0`,
      icon: <AttachMoneyIcon style={{ fontSize: '1.25rem', color: '#111111' }} />,
      badge: `${creditosVigentes.length} activos`,
      badgeClass: 'badge-pill-green'
    },
    {
      id: 'obligaciones',
      label: 'Créditos Vigentes',
      prefix: '',
      value: `${creditosVigentes.length}`,
      suffix: 'obligaciones',
      subtitle: `De ${creditosData.length} créditos totales registrados`,
      icon: <ReceiptLongIcon style={{ fontSize: '1.25rem', color: '#344054' }} />,
      badge: `${creditosData.length} total`,
      badgeClass: 'badge-pill-navy'
    },
    {
      id: 'pagos',
      label: 'Pagos Última Semana',
      prefix: '',
      value: `${ultimosPagos.length}`,
      suffix: 'recaudos',
      subtitle: ultimosPagos.length > 0 ? `Más reciente: ${formatearFecha(ultimosPagos[0].fechaTx)}` : 'Sin pagos en los últimos 7 días',
      icon: <AccountBalanceIcon style={{ fontSize: '1.25rem', color: '#027A48' }} />,
      badge: ultimosPagos.length > 0 ? formatearMonto(ultimosPagos[0].vlrTx) : '$0',
      badgeClass: 'badge-pill-green'
    },
    {
      id: 'clientes',
      label: 'Titulares Registrados',
      prefix: '',
      value: `${clientesData.length}`,
      suffix: 'deudores',
      subtitle: 'Deudores y codeudores vinculados a créditos',
      icon: <PeopleIcon style={{ fontSize: '1.25rem', color: '#D71920' }} />,
      badge: `${clientesData.length} personas`,
      badgeClass: 'badge-pill-red'
    }
  ];

  const quickActions = [
    {
      id: 'credits-create',
      title: 'Nuevo Crédito',
      desc: 'Liquidación y apertura de obligación',
      icon: <AddCircleOutlineIcon style={{ fontSize: '1.4rem', color: '#111111' }} />,
      link: '/credits'
    },
    {
      id: 'credits-search',
      title: 'Consultar Cartera',
      desc: 'Búsqueda de créditos por ID o documento',
      icon: <SearchIcon style={{ fontSize: '1.4rem', color: '#111111' }} />,
      link: '/search/credits'
    },
    {
      id: 'clients-create',
      title: 'Nuevo Titular',
      desc: 'Registro de nuevo cliente deudor',
      icon: <PeopleIcon style={{ fontSize: '1.4rem', color: '#111111' }} />,
      link: '/clients'
    },
    {
      id: 'documents',
      title: 'Generar Informes',
      desc: 'Extractos, estados de cuenta y paz y salvos',
      icon: <DescriptionIcon style={{ fontSize: '1.4rem', color: '#111111' }} />,
      link: '/documents'
    },
    {
      id: 'uvr',
      title: 'Tasas UVR',
      desc: 'Valor UVR oficial Banco de la República',
      icon: <TrendingUpIcon style={{ fontSize: '1.4rem', color: '#111111' }} />,
      link: '/uvr'
    },
    {
      id: 'closedPeriods',
      title: 'Cierre de Periodo',
      desc: 'Consolidación mensual y apertura de ciclo',
      icon: <EventAvailableIcon style={{ fontSize: '1.4rem', color: '#111111' }} />,
      link: '/closedPeriods'
    }
  ];

  // ─── Acceso denegado para roles sin permiso ───
  if (!actUsrToken) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Acceso denegado — por favor inicie sesión</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', maxWidth: '1240px', margin: '0 auto' }}>
      
      {/* ─── Encabezado de Bienvenida ─── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.5rem' }}>
            <span style={{
              padding: '0.2rem 0.65rem',
              borderRadius: '9999px',
              background: '#F2F4F7',
              border: '1px solid #EAECF0',
              color: '#344054',
              fontSize: '0.72rem',
              fontWeight: 600,
              letterSpacing: '0.02em'
            }}>
              Inmobiliaria Mobilia
            </span>
            <span style={{ fontSize: '0.80rem', color: '#667085' }}>
              Ciclo Contable: <strong style={{ color: '#111111' }}>{new Date().toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}</strong>
            </span>
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#111111', letterSpacing: '-0.03em', margin: 0 }}>
            Panel de Control de Cartera
          </h1>
          <p style={{ fontSize: '0.90rem', color: '#667085', marginTop: '0.35rem' }}>
            Monitoreo en tiempo real de obligaciones, recaudos y tablas de amortización
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <Link to="/documents" className="btn-mobilia-secondary" style={{ textDecoration: 'none' }}>
            <DescriptionIcon style={{ fontSize: '1rem', color: '#667085' }} /> Informes Oficiales
          </Link>
          <Link to="/credits" className="btn-mobilia-primary" style={{ textDecoration: 'none' }}>
            <AddCircleOutlineIcon style={{ fontSize: '1.05rem' }} /> Nuevo Crédito
          </Link>
        </div>
      </div>

      {/* ─── KPIs ─── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.25rem'
      }}>
        {metricCards.map((card) => (
          <div
            key={card.id}
            className="glass-panel"
            style={{
              padding: '1.4rem 1.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.8rem',
              borderRadius: '20px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{
                width: '38px',
                height: '38px',
                borderRadius: '11px',
                background: '#F9FAFB',
                border: '1px solid #EAECF0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {card.icon}
              </div>
              <span className={card.badgeClass}>{card.badge}</span>
            </div>
            <div>
              <div style={{ fontSize: '0.70rem', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {card.label}
              </div>
              <div
                className="tabular-nums"
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '0.22rem',
                  fontSize: '1.20rem',
                  fontWeight: 600,
                  color: '#111111',
                  marginTop: '0.35rem',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.25
                }}
              >
                {card.prefix && (
                  <span style={{ fontSize: '0.85em', fontWeight: 500, color: '#667085' }}>
                    {card.prefix}
                  </span>
                )}
                <span>{loading ? '...' : card.value}</span>
                {card.suffix && (
                  <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#667085', marginLeft: '0.12rem' }}>
                    {card.suffix}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.74rem', color: '#667085', marginTop: '0.25rem' }}>
                {card.subtitle}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── Acciones Rápidas ─── */}
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.85rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111111', margin: 0, letterSpacing: '-0.01em' }}>
              Acciones Rápidas
            </h2>
            <span style={{ fontSize: '0.74rem', color: '#98A2B3', fontWeight: 500 }}>
              • Acceso directo a procesos de cartera
            </span>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem'
        }}>
          {quickActions.map((action) => (
            <Link
              key={action.id}
              to={action.link}
              style={{
                textDecoration: 'none',
                background: '#FFFFFF',
                border: '1px solid #EAECF0',
                borderRadius: '18px',
                padding: '1.4rem 1.15rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: '0.8rem',
                cursor: 'pointer',
                transition: 'all 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
                boxShadow: '0 2px 8px rgba(16, 24, 40, 0.02)',
                color: 'inherit'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 24px rgba(16, 24, 40, 0.06)';
                e.currentTarget.style.borderColor = 'rgba(215, 25, 32, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 24, 40, 0.02)';
                e.currentTarget.style.borderColor = '#EAECF0';
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: '#F9FAFB',
                border: '1px solid #EAECF0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {action.icon}
              </div>
              <div>
                <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#111111' }}>
                  {action.title}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#667085', marginTop: '0.2rem', lineHeight: 1.35 }}>
                  {action.desc}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* ─── Tablas con Tabs ─── */}
      <div className="glass-panel" style={{ padding: '1.75rem', display: 'flex', flexDirection: 'column', gap: '1.35rem', borderRadius: '20px' }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #EAECF0',
          paddingBottom: '1rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{
            display: 'flex',
            background: '#F2F4F7',
            padding: '3px',
            borderRadius: '9999px',
            gap: '2px'
          }}>
            {[
              { key: 'creditos', label: `Créditos (${creditosVigentes.length})` },
              { key: 'pagos', label: `Pagos (${ultimosPagos.length})` },
              { key: 'clientes', label: `Titulares (${clientesData.length})` }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '0.42rem 1rem',
                  borderRadius: '9999px',
                  border: 'none',
                  background: activeTab === tab.key ? '#FFFFFF' : 'transparent',
                  color: activeTab === tab.key ? '#111111' : '#667085',
                  fontWeight: activeTab === tab.key ? 600 : 500,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  boxShadow: activeTab === tab.key ? '0 1px 3px rgba(16, 24, 40, 0.06)' : 'none',
                  transition: 'all 0.15s ease',
                  fontFamily: 'inherit'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <Link
            to={activeTab === 'creditos' ? '/search/credits' : activeTab === 'pagos' ? '/credits' : '/search/clients'}
            className="btn-mobilia-secondary"
            style={{ fontSize: '0.78rem', padding: '0.38rem 0.85rem', textDecoration: 'none' }}
          >
            <SearchIcon style={{ fontSize: '0.9rem', color: '#667085' }} /> Ver Todos en Consulta
          </Link>
        </div>

        {/* Tabla: Créditos */}
        {activeTab === 'creditos' && (
          <div className="modern-table-container">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>N° Crédito</th>
                  <th>Saldo a Capital</th>
                  <th>Plazo</th>
                  <th style={{ textAlign: 'right' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {creditosVigentes.map((cred) => (
                  <tr key={cred.id}>
                    <td>
                      <Link
                        to={`/credits/${cred.id}`}
                        style={{ color: '#111111', fontWeight: 600, textDecoration: 'underline', textDecorationColor: 'rgba(215, 25, 32, 0.4)' }}
                      >
                        {cred.id}
                      </Link>
                    </td>
                    <td className="tabular-nums" style={{ fontWeight: 600, color: '#111111', fontSize: '0.84rem' }}>
                      {formatearMonto(cred.vlrCapital)}
                    </td>
                    <td style={{ color: '#667085', fontSize: '0.82rem' }}>{cred.plazo} meses</td>
                    <td style={{ textAlign: 'right' }}>
                      <Link
                        to={`/credits/${cred.id}`}
                        style={{
                          background: '#FFFFFF',
                          border: '1px solid #EAECF0',
                          color: '#344054',
                          padding: '0.32rem 0.72rem',
                          borderRadius: '8px',
                          fontSize: '0.76rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          display: 'inline-block'
                        }}
                      >
                        Ver Ficha
                      </Link>
                    </td>
                  </tr>
                ))}
                {creditosVigentes.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#667085', padding: '2rem' }}>
                    {loading ? 'Cargando créditos...' : 'No hay créditos vigentes'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Tabla: Pagos */}
        {activeTab === 'pagos' && (
          <div className="modern-table-container">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>N° Crédito</th>
                  <th>Monto Pagado</th>
                  <th>Fecha Transacción</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {ultimosPagos.map((pago) => (
                  <tr key={pago.id}>
                    <td>
                      <Link
                        to={`/credits/${pago.credito}`}
                        style={{ color: '#111111', fontWeight: 600, textDecoration: 'underline', textDecorationColor: 'rgba(215, 25, 32, 0.4)' }}
                      >
                        {pago.credito}
                      </Link>
                    </td>
                    <td className="tabular-nums" style={{ fontWeight: 600, color: '#027A48', fontSize: '0.84rem' }}>
                      {formatearMonto(pago.vlrTx)}
                    </td>
                    <td style={{ color: '#667085' }}>{formatearFecha(pago.fechaTx)}</td>
                    <td>
                      <span className="badge-pill-green">
                        <CheckCircleIcon style={{ fontSize: '0.72rem' }} /> Aplicado
                      </span>
                    </td>
                  </tr>
                ))}
                {ultimosPagos.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: '#667085', padding: '2rem' }}>
                    {loading ? 'Cargando pagos...' : 'No hay pagos registrados'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Tabla: Clientes */}
        {activeTab === 'clientes' && (
          <div className="modern-table-container">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Tipo Doc.</th>
                  <th>Nombre</th>
                  <th>Documento</th>
                </tr>
              </thead>
              <tbody>
                {clientesData.map((cli, idx) => (
                  <tr key={`${cli.documento}-${idx}`}>
                    <td>
                      <span className="badge-pill-navy">{cli.tipoDocumento || 'CC'}</span>
                    </td>
                    <td><strong style={{ color: '#111111' }}>{cli.nombre}</strong></td>
                    <td className="tabular-nums" style={{ fontWeight: 600, color: '#111111' }}>{cli.documento}</td>
                  </tr>
                ))}
                {clientesData.length === 0 && (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: '#667085', padding: '2rem' }}>
                    {loading ? 'Cargando clientes...' : 'No hay clientes registrados'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
